import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PatientVisitList from './PatientVisitList';
import PatientAdmissionList from './PatientAdmissionList';
import LabTestRequestList from '../Lab/LabTestRequestList';
import PatientBillingList from './PatientBillingList';
import ProcedureList from '../Procedures/ProcedureList';
import PatientMessaging from '../PatientMessaging';
import AskAssistantButton from '../Messages/AskAssistantButton';
import PatientReportPanel from './PatientReportPanel';
import '../../styles/PatientCard.css';
import { useAuth } from '../../hooks/useAuth';

const PatientCard = ({
  patientId: patientIdProp,
  patientNumber: patientNumberProp,
  patient: patientProp,
} = {}) => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  
  // State for patient data and loading
  const [patient, setPatient] = useState(patientProp || state?.patient || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State to track the active tab. Default is 'visits'.
  const [activeTab, setActiveTab] = useState('visits');
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    const fetchPatientData = async () => {
      if (patientProp) {
        setPatient(patientProp);
        return;
      }

      const patientId = patientIdProp || searchParams.get('patientId');
      const patientNumber = patientNumberProp || searchParams.get('patientNumber');
      if (!patientId && !patientNumber) return;
      if (patient && !patientIdProp && !patientNumberProp && !patientProp) return;

      setLoading(true);
      setError(null);

      try {
        if (patientId) {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/patients/patient/${patientId}`
          );
          setPatient(response.data);
        } else {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/patients`,
            { params: { patient_number: patientNumber } }
          );
          const rows = Array.isArray(response.data) ? response.data : [];
          const match =
            rows.find(
              (p) =>
                String(p.patient_number || '').toUpperCase() ===
                String(patientNumber).toUpperCase()
            ) || rows[0];
          if (!match) {
            setError('Patient not found for that patient number.');
          } else {
            setPatient(match);
          }
        }
      } catch (err) {
        console.error('Error fetching patient data:', err);
        setError('Failed to load patient information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPatientData();
  }, [patientProp, patientIdProp, patientNumberProp, searchParams]);

  // Loading state
  if (loading) {
    return (
      <div className="patient-card">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading patient information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="patient-card">
        <div style={{ padding: '20px', textAlign: 'center', color: '#dc3545' }}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // No patient data available
  if (!patient) {
    return (
      <div className="patient-card">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>No patient information provided.</p>
        </div>
      </div>
    );
  }

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'visits':
        return <PatientVisitList patient={patient} />;
      case 'admissions':
        return <PatientAdmissionList patient={patient} />;
      case 'labRequests':
        return <LabTestRequestList patient={patient} viewName='PatientCard' />;
      case 'billing':
        return <PatientBillingList patient={patient} />;
      case 'surgeries':
        return <ProcedureList patient={patient} />;
      default:
        return null;
    }
  };

  return (
    <div className="patient-card">
      <header className="patient-card-header">
        <div className="patient-header-content">
          <div className="patient-details">
            <h2>Patient Details</h2>
            <p><strong>Patient Number:</strong> {patient.patient_number}</p>
            <p>
              <strong>Name:</strong> {patient.patient_first_name} {patient.patient_last_name}
            </p>
            <p><strong>Date of Birth:</strong> {patient.patient_dob}</p>
            <p><strong>Patient Type:</strong> {patient.patient_type}</p>
            <p><strong>Company Name:</strong> {patient.company}</p>
            {/* Additional patient details can be added here */}
          </div>
          <div className="patient-actions">
            <PatientMessaging patient={patient} />
            <AskAssistantButton patient={patient} />
            {hasPermission('/ai-assist/patient-report', 'Execute') && (
              <button
                type="button"
                onClick={() => setShowReport(true)}
                className="btn-patient-report"
                title="Generate a patient report"
              >
                Patient Report
              </button>
            )}
          </div>
        </div>
      </header>

      <nav className="patient-card-tabs">
        <ul>
          {hasPermission('/patient-card/Tabs/Visits', 'View') && (
            <li
              className={activeTab === 'visits' ? 'active' : ''}
              onClick={() => setActiveTab('visits')}
            >
              Visits
            </li>
          )}
          {hasPermission('/patient-card/Tabs/Admissions', 'View') && (
            <li
              className={activeTab === 'admissions' ? 'active' : ''}
              onClick={() => setActiveTab('admissions')}
            >
              Admissions
            </li>
          )}
          {hasPermission('/patient-card/Tabs/LabRequests', 'View') && (
            <li
              className={activeTab === 'labRequests' ? 'active' : ''}
              onClick={() => setActiveTab('labRequests')}
            >
              Lab Requests
            </li>
          )}
          {hasPermission('/patient-card/Tabs/Billing', 'View') && (
            <li
              className={activeTab === 'billing' ? 'active' : ''}
              onClick={() => setActiveTab('billing')}
            >
              Billing
            </li>
          )}
          {hasPermission('/patient-card/Tabs/Surgeries', 'View') && (
            <li
              className={activeTab === 'surgeries' ? 'active' : ''}
              onClick={() => setActiveTab('surgeries')}
            >
              Surgeries
            </li>
          )}
        </ul>
      </nav>

      <div className="patient-card-content">
        {renderActiveTab()}
      </div>

      {hasPermission('/ai-assist/patient-report', 'Execute') && (
        <PatientReportPanel
          visible={showReport}
          onClose={() => setShowReport(false)}
          source="outpatient"
          patientId={patient.patient_id}
        />
      )}
    </div>
  );
};

export default PatientCard;
